/**
 * `dispatchAuthSubmit` — the Authentication-tab submit dispatcher
 * (Multi-Add Phase 2, spec §4.2 / §5). Pure + fully dependency-injected
 * so every branch is unit-testable with DI fakes for `probeOnvifDevice` /
 * `linkIrisDevice` (per `ai-code-requires-tests` + `esm-jest-mock-patterns`)
 * without rendering `<AuthenticationContent>` or booting the heavy
 * `@studio/models/device` tree.
 *
 * Per `think-in-systems` (ai.knowledge/global): this is the framework-
 * agnostic core; `authentication-content.tsx` is the React adapter that
 * gathers the real dependencies and renders the form (mirrors the
 * `chip-state.ts` ↔ `use-chip-state.ts` split W3 introduced).
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
import {
  applyCredentialsToCandidate,
  buildBulkCandidate
} from '#~/video/components/add/source/utils.js';
import {
  probeOnvifDevice,
  type TransientsSnapshot
} from '#~/video/components/add/source/onvif-probe.js';
import { linkIrisDevice } from '#~/video/components/add/source/link-iris.js';
import {
  DIRECT_LINK_CREDENTIALS_MESSAGE,
  DIRECT_LINK_FAILED_MESSAGE,
  DIRECT_LINK_UNREACHABLE_MESSAGE,
  directLinkCameraErrorMessage
} from '#~/agent/components/bridge/direct-link-transport-error.js';
import { addDevice } from '#~/video/components/add/store/utils.js';
import {
  verificationMessage,
  verificationSeverity,
  type AspectOutcomes,
  type ConnectionVerification
} from '../connection-verification.js';
import { replaceDevice, watchDeviceConnection } from '#~/video/components/settings/utils.js';
import { toCandidate, toDevice } from '#~/device/index.js';
import type { TransitoryPlaceholder } from '#~/device/transitory.js';
import { removeTransitoryCamera } from '#~/device/commands/pending.js';
import { isElectronApp } from '#~/electron/utils.js';
import { EVENT_NAMES } from '#~/analytics/constants/event-names.js';
import { emitMultiAddAnalytics } from '#~/analytics/utils/emit-multi-add-analytics.js';
import { isCausedBy } from '@studio/js-utils/logger';
import type { Discovery, Discovery$Control } from '#~/device/discovery/index.js';
import type { Device, Minimist } from '#~/device/index.js';
import type { Credentials as M_Credentials } from '@studio/models/auth';
import type { Agent } from '@studio/models/agent';
import type { AppId } from '@studio/models/app';
import type { Room } from '@studio/models/room';
import { generateId } from '@studio/models';
import type { Configuration as RtspConfig } from '@studio/models/video/rtsp';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { defaults } from 'lodash-es';

/**
 * Shown when a browser client tries to authenticate a pending Iris camera:
 * linking reaches the camera through the desktop app's local network access.
 */
export const IRIS_DESKTOP_ONLY_MESSAGE = 'Authenticate this camera from the desktop app.';

/**
 * Outcome of `dispatchAuthSubmit`. The success branches are `committed`
 * (§5.1 ONVIF probe+commit), `linked` (§5.2 Iris link), `reloaded` (§4.3 real
 * device committed-and-reloaded in place via `replaceDevice`), and `applied`
 * (§4.2 else — credentials folded into the draft with no reload because of a missing precondition). `error` carries an inline,
 * user-facing message and leaves the placeholder/device in place so the user
 * can retry (spec §5 — every failure is retryable). Every kind renders an
 * explicit UI arm — no silent success (rule `settle-every-terminal-branch`).
 */
export type AuthDispatchResult =
  // `deviceId` is the freshly-committed REAL device id (ENG-4486 Fix A.3):
  // the ONVIF placeholder→real transition mints it here, and the React
  // adapter needs it to migrate the multi-add origin from the placeholder id
  // to the real id. Surfacing the id it already computes adds no coupling.
  | {
      kind: 'committed';
      deviceId: Device['id'];
      device: Device;
      verification: ConnectionVerification;
      verificationAspects?: AspectOutcomes;
    }
  | { kind: 'linked' }
  // `verification` is set when the reload saved but the camera only partly or never confirmed it
  // (`video-verified` / `unverified`): a warning, not an error. Absent ⇒ verified.
  | {
      kind: 'reloaded';
      resolvedVideo?: NonNullable<Device['video']>['config'];
      verification?: ConnectionVerification;
      verificationAspects?: AspectOutcomes;
    }
  | { kind: 'applied' }
  | { kind: 'error'; message: string };

/**
 * Dependencies for `dispatchAuthSubmit`. The probe/link/commit/remove/id
 * seams default to the real implementations; tests override them with DI
 * fakes (per `esm-jest-mock-patterns` — inject the boundary functions).
 */
export interface AuthDispatchDeps {
  /** Room the (real) device is committed into / the link is bound to. */
  roomId: T_Maybe<Room['id']>;
  /** Auth0 access token for `addDevice` / `linkIrisDevice`. */
  token: string;
  /** Studio appId — `getTransientId(appId)` inside the ONVIF probe needs it. */
  appId: AppId;
  /** Fallback agent id when the discovery carries none. */
  studioAgentId: T_Maybe<Agent['id']>;
  /** Read-only accessor to the transient-device slice the ONVIF probe polls. */
  transients: TransientsSnapshot;
  /**
   * The frozen `lifecycle.original` device for the real-device (`!placeholder`)
   * path (spec §4.3). The credentials-only reload replacement is built from a
   * clone of THIS — never the live candidate, which may hold unrelated draft
   * edits (R2) and cannot be read synchronously post-write (R3). Absent for the
   * placeholder paths, and when absent the real path degrades to a draft-only
   * fold. Its `id` is the live device's id, so the reload updates it in place.
   */
  original?: T_Maybe<Device>;

  // ---- DI seams (default to the real imports) ----
  /** ONVIF probe (spec §5.1). */
  probeOnvif?: typeof probeOnvifDevice;
  /** Iris agent-link (spec §5.2). */
  linkIris?: typeof linkIrisDevice;
  /**
   * Bug 2 (spec §2.3): confirm an unlink-then-relink for an already-linked
   * embedded camera. Threaded verbatim into `linkIrisDevice`, which invokes it
   * only on its already-linked branch (the dispatcher stays confirmation-
   * agnostic). Supplied by `authentication-content.tsx` as the single-add
   * `prompt.confirm`; absent ⇒ the unchanged already-linked bail.
   */
  confirmRelink?: () => Promise<boolean>;
  /**
   * Real-device commit-and-reload (spec §4.3). Credentials-only remove+add of
   * the live device via `replaceDevice` so the tile chip — derived from live
   * bridge state-sync — re-evaluates without a separate Save. Defaults to the
   * real import; tests inject a fake. Same DI pattern as `probeOnvif`/`linkIris`.
   */
  replaceRealDevice?: typeof replaceDevice;
  /** Commit the real RTSP device post-probe. */
  commitDevice?: typeof addDevice;
  /**
   * Remove the placeholder on success: its tile, and the record a bridge
   * persists for it. Awaited, because the Iris success path reloads the Studio
   * right after and the record must already be gone. Defaults to
   * `removeTransitoryCamera` (bounded wait).
   */
  removePlaceholder?: (id: Device['id']) => void | Promise<void>;
  /** Whether this client is the desktop app (Iris links need it). Defaults to `isElectronApp`. */
  isDesktopApp?: () => boolean;
  /** Fresh device id for the committed real device. */
  generateDeviceId?: () => Device['id'];
  /**
   * Analytics seam (Multi-Add Phase 2, W5). Fired on the placeholder →
   * authenticated transition — i.e. when a transitory needs-auth device is
   * resolved into a real/linked device via this Authentication-tab submit
   * (reached from the masthead notification deep-link). Defaults to
   * `emitMultiAddAnalytics`; tests inject a jest.fn(). Per
   * `esm-jest-mock-patterns` (ai.knowledge/global).
   */
  emit?: typeof emitMultiAddAnalytics;
  /**
   * Called once an ONVIF placeholder left by bulk add is saved as a real
   * camera, with the device data that was saved. The caller sends `device_add`
   * from it so the camera counts as added (ENG-4950).
   */
  onCameraSaved?: (device: Minimist) => void;
}

/** Shown when the camera accepted the credentials but Iris could not register the link. */
const IRIS_CLOUD_LINK_FAILED_MESSAGE =
  'The camera accepted the credentials, but Iris could not link it to this Studio. Try again.';

/**
 * Map a `linkIrisDevice` failure reason to a differentiated inline
 * message (spec §5.2 — lockout / creds-rejected / already-linked must
 * read differently). An `error` reads as its cause; its `message` is raw
 * IPC/API text for the log and is never shown. Copy for the camera request
 * is shared with the single-add dialog (direct-link-transport-error.ts).
 */
const irisFailureMessage = (
  result: Extract<Awaited<ReturnType<typeof linkIrisDevice>>, { kind: 'failed' }>
): string => {
  switch (result.reason) {
    case 'locked':
      // Lockout window from the camera (§5.2) — surface the cooldown.
      return result.lockoutSeconds
        ? `Device is locked. Try again in ${result.lockoutSeconds}s.`
        : 'Device is locked from too many attempts. Try again later.';
    case 'auth':
      // The camera's own wording when it sent one.
      return result.message ?? DIRECT_LINK_CREDENTIALS_MESSAGE;
    case 'already-linked':
      // §12: bulk-add does not steal from another studio; direct the user
      // to single-add. Distinct from the real-already-linked "not
      // supported" copy (that path never reaches the dispatcher).
      return 'This device is already linked to another studio. Unlink it first.';
    default:
      switch (result.cause) {
        case 'unreachable':
          return DIRECT_LINK_UNREACHABLE_MESSAGE;
        case 'camera':
          return directLinkCameraErrorMessage(result.status);
        case 'cloud':
          return IRIS_CLOUD_LINK_FAILED_MESSAGE;
        default:
          return DIRECT_LINK_FAILED_MESSAGE;
      }
  }
};

/** Best-effort human label for a device in an inline error message. */
const deviceLabel = (device: Device): string => device.name ?? 'the device';

/**
 * Map a failed `replaceDevice` reload to an inline, user-facing message.
 * `rolled-back`: prior config is live; `add-failed`: camera is gone;
 * `no-live-device`: never found a live id. Spec V4.2 — never silence.
 */
const replaceFailureMessage = (
  reason: 'no-live-device' | 'add-failed' | 'rolled-back',
  device: Device
): string => {
  const name = deviceLabel(device);
  if (reason === 'rolled-back') {
    return `Could not update ${name}. Previous credentials were restored.`;
  }
  if (reason === 'add-failed') {
    return `${name} was removed but could not be re-added. Re-add it to continue.`;
  }
  return `${name} is no longer available. Re-add it to authenticate.`;
};

/** Resolve video and credentials while retaining the selected ONVIF control configuration. */
export const buildProbedOnvifCandidate = (
  discovery: Discovery,
  videoConfig: unknown,
  credentials: { username: string; password: string } | undefined
): T_Maybe<Pick<Minimist, 'video' | 'control'>> => {
  // Host resolution mirrors `add/wiring.ts` (discovery.address ?? control host).
  const controlHost =
    'control' in discovery && discovery.control
      ? (discovery.control as { host?: string }).host
      : undefined;
  const host = discovery.address ?? controlHost;
  const candidate = buildBulkCandidate(discovery, undefined, host);
  if (!candidate) return undefined;

  // (1) VIDEO — probe-discovered URI with credentials stamped (VC-A61P).
  const credsPatch = credentials ? { credentials } : {};
  const videoWithCreds = {
    ...(videoConfig as Record<string, unknown>),
    ...credsPatch
  } as NonNullable<Pick<Minimist, 'video'>['video']>;

  return {
    ...candidate,
    video: videoWithCreds,
    ...(candidate.control ? { control: { ...candidate.control, ...credsPatch } } : {})
  };
};

/**
 * The Authentication-tab submit dispatcher (spec §4.2 / §5).
 *
 * @param placeholder  — the transitory placeholder for the candidate id,
 *   or `undefined` for a real (already-committed) device.
 * @param credentials  — the credentials the user typed.
 * @param applyToCandidate — the UNCHANGED Phase-1 fold for the `else`
 *   branch (`applyCredentialsToCandidate` via the settings producer). Kept
 *   as a closure so this function stays free of the React store.
 * @param deps — injected room/token/appId/transients context + seams.
 */
export const dispatchAuthSubmit = async (
  placeholder: T_Maybe<TransitoryPlaceholder>,
  credentials: T_Maybe<M_Credentials>,
  applyToCandidate: () => void,
  deps: AuthDispatchDeps
): Promise<AuthDispatchResult> => {
  // else branch (§4.2 / §4.3): a REAL (already-committed) device with bad
  // creds — no placeholder in the slice. Fold the typed creds into the
  // settings draft (UNCHANGED Phase-1 behavior, keeps the footer/tab coherent
  // for any unrelated edits), then commit-and-reload the live device directly
  // so the tile chip — derived from live bridge state-sync — re-evaluates
  // WITHOUT requiring a separate Save. Converges the real path onto the same
  // reload the transitory paths already trigger (rule `settle-every-terminal-branch`).
  if (!placeholder) {
    applyToCandidate();

    const replaceRealDevice = deps.replaceRealDevice ?? replaceDevice;
    const original = deps.original;
    // The live device's owning agent — the frozen original carries it.
    const agentId = original?.agentId ?? deps.studioAgentId;

    // Without the frozen original + a room + an agent we cannot build or
    // target the reload. Degrade to the draft-only fold (the footer's Save can
    // still reload); `applied` explicitly reports that credentials remain unverified.
    if (!original || !deps.roomId || !agentId) {
      return { kind: 'applied' };
    }

    // Build a CREDENTIALS-ONLY replacement from a clone of the frozen original
    // (spec §4.3): convert to the candidate shape, stamp ONLY the typed creds
    // via the same helper the draft fold uses, convert back. Same id ⇒ the
    // reload updates the live device in place (ENG-4356 resolves the id).
    const replacementCandidate = toCandidate(original);
    applyCredentialsToCandidate(replacementCandidate, credentials);

    // Explicit Submit retries verification even when the entered credentials are unchanged.
    let resolvedVideo: NonNullable<Device['video']>['config'] | undefined;
    if (original.control?.config.protocol === 'onvif') {
      // Validate ONVIF credentials and resolve its stream before replacing the saved camera.
      const probe = await (deps.probeOnvif ?? probeOnvifDevice)(
        {
          id: original.id as unknown as Discovery['id'],
          agentId,
          name: original.name ?? 'Camera',
          irisEnabled: false,
          control: { ...original.control.config, credentials }
        },
        credentials,
        {
          roomId: deps.roomId,
          agentId,
          appId: deps.appId,
          token: deps.token,
          transients: deps.transients
        }
      );
      if (probe.kind === 'failed') {
        return {
          kind: 'error',
          message: 'Could not authenticate the ONVIF camera. Check credentials and connection.'
        };
      }
      // An uninitialized driver has no discovered URI; replace only its provisional stream configuration.
      if (!original.control?.capabilities?.discovery?.uri) {
        resolvedVideo = { ...(probe.videoConfig as RtspConfig), credentials };
        replacementCandidate.video = resolvedVideo;
      }
    }
    const replacement = toDevice(replacementCandidate);

    let verification: ConnectionVerification = 'unverified';
    let verificationAspects: AspectOutcomes | undefined;
    try {
      const outcome = await replaceRealDevice(deps.roomId, agentId, replacement, deps.token, {
        verifyConnection: true
      });
      if (outcome?.ok === false) {
        // The reload ran but did not bind the device (no live device to
        // update, or the re-add failed after the remove). Surface inline —
        // never the silent log-and-return the raw seam used to do (V4.2).
        return { kind: 'error', message: replaceFailureMessage(outcome.reason, original) };
      }
      verification = outcome?.verification ?? 'unverified';
      verificationAspects = outcome?.verificationAspects;
      // Only a rejection or an unvouched failure is an error. Working video with unconfirmed
      // control, or no verdict at all, still saved the credentials and is reported as a warning
      // by the caller. Per `absent-data-gates-permissively` (ai.knowledge/global).
      if (verificationSeverity(verification) === 'error') {
        return { kind: 'error', message: verificationMessage(verification, verificationAspects) };
      }
    } catch {
      // `replaceDevice` itself threw (command/transport error). Surface
      // inline; the draft still holds the creds so the user can retry.
      return {
        kind: 'error',
        message: `Could not reload ${deviceLabel(original)}. Check the connection and try again.`
      };
    }

    return {
      kind: 'reloaded',
      ...(resolvedVideo ? { resolvedVideo } : {}),
      ...(verification !== 'verified' ? { verification } : {}),
      ...(verificationAspects ? { verificationAspects } : {})
    };
  }

  const probeOnvif = deps.probeOnvif ?? probeOnvifDevice;
  const linkIris = deps.linkIris ?? linkIrisDevice;
  const commitDevice = deps.commitDevice ?? addDevice;
  const removePlaceholder =
    deps.removePlaceholder ??
    ((id: Device['id']) => removeTransitoryCamera(id, async () => deps.token));
  const genId = deps.generateDeviceId ?? (() => generateId<Device['id']>());
  const emit = deps.emit ?? emitMultiAddAnalytics;

  if (!deps.roomId) {
    // No room resolved — cannot probe/commit/link. Surface inline; the
    // placeholder stays (retryable). Per `verify-and-escalate`.
    return { kind: 'error', message: 'No room available to authenticate against.' };
  }

  // ---- ONVIF (spec §5.1) ----
  if (placeholder.transitoryKind === 'onvif') {
    const discovery = placeholder.originalDiscovery;
    const agentId = (discovery.agentId ?? deps.studioAgentId ?? '') as Agent['id'];

    const result = await probeOnvif(discovery as Discovery$Control, credentials, {
      roomId: deps.roomId,
      agentId,
      appId: deps.appId,
      token: deps.token,
      transients: deps.transients
    });

    if (result.kind !== 'success') {
      // A failed probe only says this attempt failed, not that the camera
      // left: report it inline and keep the placeholder retryable. Only the
      // liveness sweep (transitory.ts) removes a placeholder and announces it.
      // The probe's raw message is an internal bridge error id (e.g.
      // "Unable to add device: tmp:...") — never surface it to the user.
      const message =
        result.reason === 'timeout'
          ? 'Could not reach the device. Try again.'
          : 'Authentication failed. Check the credentials and try again.';
      return { kind: 'error', message };
    }

    // Commit the real device with creds STAMPED into the video config
    // and the selected ONVIF control. Reuses the bulk-add success
    // shape so the URX is byte-identical.
    const resolvedCreds =
      credentials?.username !== undefined
        ? { username: credentials.username, password: credentials.password ?? '' }
        : undefined;
    const candidate = buildProbedOnvifCandidate(discovery, result.videoConfig, resolvedCreds);
    if (!candidate) {
      return { kind: 'error', message: 'Could not build the device configuration.' };
    }

    // Final commit shape mirrors `runBulkCommit`'s allSettled iteratee
    // (`add/wiring.ts:1331-1338`): candidate + name + agentId + fresh id.
    // Capture the fresh id so the `committed` result can surface it (Fix A.3
    // origin migration in the adapter).
    const realId = genId();
    const deviceData = {
      ...defaults({}, candidate, { name: placeholder.name }),
      agentId,
      id: realId
    } as Minimist;

    const verification = watchDeviceConnection(agentId, toDevice(deviceData));
    let saved: Awaited<ReturnType<typeof commitDevice>> | undefined;
    try {
      saved = await commitDevice(deps.roomId, agentId, deviceData, deps.token);
      // The device exists from here on: status updates now belong to this configuration.
      verification?.commit();
    } catch (error) {
      verification?.stop();
      const isAuthError =
        isCausedBy(error, '401: Unauthorized') ||
        isCausedBy(error, '403: Forbidden') ||
        isCausedBy(error, '400: Bad Request');

      return {
        kind: 'error',
        message: isAuthError
          ? 'Authentication failed. Check the credentials and try again.'
          : 'Could not add the device. Try again.'
      };
    }
    // ENG-4950: a confirmed save counts as camera added, as in single add.
    // Inside try so a telemetry failure never fails the sign-in.
    try {
      if (saved?.kind === 'saved') deps.onCameraSaved?.(deviceData);
    } catch {
      /* Telemetry is not persistence. */
    }

    const verdict = (await verification?.wait()) ?? 'unverified';

    // Real device is now a normal device; drop the placeholder (§5.1.3).
    await removePlaceholder(placeholder.id);
    // Placeholder → authenticated transition (W5): the needs-auth device
    // reached from the notification deep-link is now fully configured.
    if (verdict === 'verified') {
      emit(EVENT_NAMES.DEVICE_CONFIGURATION_COMPLETED_FROM_NOTIFICATION, {
        deviceId: placeholder.id.toString(),
        deviceName: placeholder.name,
        protocol: 'onvif',
        studioId: deps.roomId?.toString()
      });
    }
    // The open editor must adopt the same configuration that addDevice sent,
    // including the probed stream and credentials, rather than its placeholder.
    return {
      kind: 'committed',
      deviceId: realId,
      device: toDevice(deviceData),
      verification: verdict,
      ...(verification ? { verificationAspects: verification.aspects() } : {})
    };
  }

  // ---- Iris (spec §5.2) ----
  if (placeholder.transitoryKind === 'iris') {
    // A browser cannot reach the camera's link API; say where to authenticate instead.
    if (!(deps.isDesktopApp ?? (() => isElectronApp()))()) {
      return { kind: 'error', message: IRIS_DESKTOP_ONLY_MESSAGE };
    }

    // `linkIrisDevice`'s 2nd param is a REQUIRED `{username,password}`
    // (spec §9) — coerce the optional editor creds to strings.
    const creds = {
      username: credentials?.username ?? '',
      password: credentials?.password ?? ''
    };
    const result = await linkIris(placeholder.originalDiscovery, creds, {
      roomId: deps.roomId,
      token: deps.token,
      // Bug 2 (spec §2.3): let an already-linked camera relink from Advanced
      // Settings. link-iris runs this only on its already-linked branch. Spread
      // conditionally so the key is OMITTED (not set to `undefined`) when absent
      // — required under `exactOptionalPropertyTypes` for the optional seam.
      ...(deps.confirmRelink ? { confirmRelink: deps.confirmRelink } : {})
    });

    if (result.kind !== 'success') {
      // failed(locked | auth | already-linked | error) — differentiated
      // inline error; placeholder stays, retryable (§5.2). `error` covers any
      // thrown link failure (transport, cloud, malformed reply), so it is
      // never read as the camera having left the network.
      return { kind: 'error', message: irisFailureMessage(result) };
    }

    // Success — remove the placeholder; the camera's embedded agent joins
    // via state-sync and announces its real device(s), possibly >1 (§5.2).
    //
    // ENG-4486 Fix A.3 — IRIS ORIGIN MIGRATION DEFERRED (follow-up):
    // Unlike the ONVIF path, iris mints NO single real id at commit time —
    // the linked device(s) arrive ASYNCHRONOUSLY via state-sync (and there
    // may be >1). So there is no id to hand the multi-add origin to here.
    // Registering the real iris origin(s) must happen where state-sync
    // announces the linked device(s) (the room-provider device upsert), which
    // is out of scope for this change and NOT attempted now. Consequence:
    // post-add "needs configuration" toasts do not yet fire for iris cameras
    // authenticated from Advanced Settings (the ONVIF bench repro — a
    // VC-A61P — is covered). Tracked as a follow-up.
    await removePlaceholder(placeholder.id);
    // Placeholder → authenticated transition (W5): the needs-auth Iris
    // camera reached from the notification deep-link is now linked.
    emit(EVENT_NAMES.DEVICE_CONFIGURATION_COMPLETED_FROM_NOTIFICATION, {
      deviceId: placeholder.id.toString(),
      deviceName: placeholder.name,
      protocol: 'iris',
      studioId: deps.roomId?.toString()
    });
    return { kind: 'linked' };
  }

  // Unknown future `transitoryKind` — fall back to the Phase-1 fold so we
  // never silently no-op. Per `verify-and-escalate`.
  applyToCandidate();
  return { kind: 'applied' };
};
